import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Loader, Menu, Text, UnstyledButton } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useCallback } from "react"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

export function MeetingSourceLanguage({
  languageCode,
  displayName,
  options,
  isPending = false,
  onChangeLanguage,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  const handlePick = useCallback(
    (code: string, label: string): void => {
      if (code === languageCode || isPending) return
      modals.openConfirmModal({
        title: i18n._(msg`Change meeting language?`),
        children: (
          <Text size="sm">
            <Trans>Regenerates the summary and questions in {label}. Uses credits.</Trans>
          </Text>
        ),
        labels: { confirm: i18n._(msg`Change`), cancel: i18n._(msg`Cancel`) },
        onConfirm: () => onChangeLanguage(code),
      })
    },
    [i18n, isPending, languageCode, onChangeLanguage],
  )

  return (
    <Menu shadow="md" position="bottom-end" disabled={isPending}>
      <Menu.Target>
        <UnstyledButton
          className={classes.trigger}
          data-pending={isPending || undefined}
          aria-label={i18n._(msg`Change spoken language`)}
        >
          {isPending ? <Loader size={12} /> : null}
          <Text className={classes.label} span>
            <Trans>Spoken: {displayName}</Trans>
          </Text>
          <Text className={classes.sep} span>
            ·
          </Text>
          <Text className={classes.change} span>
            <Trans>Change</Trans>
          </Text>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Trans>Spoken language</Trans>
        </Menu.Label>
        {options.map((opt) => (
          <Menu.Item
            key={opt.code}
            onClick={() => handlePick(opt.code, opt.label)}
            disabled={opt.code === languageCode}
          >
            {opt.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}
